// src/components/latest-results/DiabeticGlucoseRow.jsx
import React from "react";

export default function DiabeticGlucoseRow({
  detail,
  valuesMap = {},
  onChange,
  isCalculated,
  renderUnitRange,
  rowLoading,
  onFetchOne,
}) {
  const subTests = Array.isArray(detail?.subTests) ? detail.subTests : [];
  const options = subTests.filter(
    (s) =>
      s?.name === "Post Prandial Glucose" || s?.name === "Fasting Glucose"
  );

  const [selectedId, setSelectedId] = React.useState(
    options[0]?.ParameterID || ""
  );

  const currentValue = valuesMap[selectedId] ?? "";
  const canFetch =
    typeof detail?.autoFetchKey === "string" &&
    detail.autoFetchKey.trim() !== "" &&
    detail.autoFetchKey.trim().toUpperCase() !== "N/A";

  const parseReferenceRange = (referenceRange) => {
    if (typeof referenceRange !== "string") return null;
    const trimmed = referenceRange.trim();
    if (!trimmed || trimmed.toLowerCase() === "n/a" || trimmed === "-") {
      return null;
    }
    if (trimmed.includes(":")) return null;

    const upperOnly = trimmed.match(/^\s*<\s*(-?\d*\.?\d+)\s*$/);
    if (upperOnly) {
      const limit = parseFloat(upperOnly[1]);
      if (!Number.isFinite(limit)) return null;
      return { upper: limit, lower: null, type: "upper" };
    }

    const lowerOnly = trimmed.match(/^\s*>\s*(-?\d*\.?\d+)\s*$/);
    if (lowerOnly) {
      const limit = parseFloat(lowerOnly[1]);
      if (!Number.isFinite(limit)) return null;
      return { lower: limit, upper: null, type: "lower" };
    }

    const match = trimmed.match(
      /(-?\d*\.?\d+)\s*[\u2013-]\s*(-?\d*\.?\d+)/
    );
    if (!match) return null;

    const first = parseFloat(match[1]);
    const second = parseFloat(match[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

    const lower = Math.min(first, second);
    const upper = Math.max(first, second);
    return { lower, upper, type: "range" };
  };

  const selectedDetail = options.find((s) => s?.ParameterID === selectedId);
  const range = parseReferenceRange(
    selectedDetail?.referenceRange || detail?.referenceRange
  );
  const numericValue =
    typeof currentValue === "number"
      ? currentValue
      : currentValue !== null && currentValue !== undefined
      ? parseFloat(currentValue)
      : NaN;

  const showVarianceWarning = (() => {
    if (!range || !Number.isFinite(numericValue)) return false;
    if (range.type === "upper") {
      return numericValue > range.upper * 1.3;
    }
    if (range.type === "lower") {
      return numericValue < range.lower * 0.7;
    }
    return (
      numericValue > range.upper * 1.3 || numericValue < range.lower * 0.7
    );
  })();

  const handleSelect = (e) => {
    const next = e.target.value;
    if (next === selectedId) {
      return;
    }

    if (selectedId) {
      onChange?.(selectedId, "");
    }

    setSelectedId(next);
    onChange?.(next, "");
  };

  const handleInput = (e) => {
    const v = e.target.value;
    onChange?.(selectedId, v);
  };

  return (
    <div className="flex items-center gap-4 py-1">
      {/* Test Name */}
      <div className="w-1/5">
        <span>{detail.name}</span>
        {showVarianceWarning ? (
          <div className="mt-1 text-[15px] leading-tight text-red-600">
            <span>Inappropriate value detected. Consider retesting</span>
          </div>
        ) : null}
      </div>

      {/* Dropdown + Input aligned horizontally */}
      <div className="flex items-center w-2/6 ml-[6%] gap-2">
        <select
          value={selectedId}
          onChange={handleSelect}
          className="w-[52%] rounded-md px-2 py-2 text-center bg-white border"
        >
          {options.length === 0 ? (
            <option value="">No subtests</option>
          ) : (
            options.map((s) => (
              <option key={s.ParameterID} value={s.ParameterID}>
                {s.name}
              </option>
            ))
          )}
        </select>

        <div className="flex flex-1 min-w-0 items-center gap-2">
          <input
            type={
              detail.inputType?.toLowerCase() === "number" ? "number" : "text"
            }
            value={currentValue}
            onChange={handleInput}
            disabled={!selectedId}
            className={`flex-1 min-w-0 rounded-md px-3 py-2 text-center transition-colors ${
              isCalculated(selectedId) ? "bg-yellow-100" : "bg-[#D7D4F8]"
            }`}
            placeholder={
              isCalculated(selectedId) ? "Auto-calculated" : "Enter Result"
            }
          />
        </div>
      </div>

      {/* Unit and Range */}
      <div className="w-1/6 text-right text-sm text-[#845BB3]">
        {renderUnitRange(detail)}
      </div>

      {/* Fetch Button */}
      <div className="w-1/6 text-right">
        {canFetch ? (
          <button
            onClick={() =>
              onFetchOne?.({ ...detail, ParameterID: selectedId })
            }
            disabled={rowLoading === selectedId || !selectedId}
            className="px-3 py-1 bg-[#845BB3] text-white rounded-md text-sm disabled:opacity-60"
          >
            {rowLoading === selectedId ? "Fetching..." : "Fetch Result"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

